import { Box, Divider, Flex, HStack, Popover, Skeleton } from '@chakra-ui/react'

import { ClickToCopyPopover } from '@/components/layouts/Copy/ClickToCopyPopover'
import { PopoverTrigger } from '@/components/layouts/Copy/PopoverTrigger'

interface DrawerBreadcrumbIProps {
  dataTestId?: string
  disabled?: boolean
  hideDivider?: boolean
  label: string
  isLoading: boolean
  onClick?: () => void
  tooltipLabel?: string
  isTruncated?: boolean
  maxWidth: string | number
  fontSize?: string
}

// Space (in px) reserved for the navigation chevron icon when breadcrumb is shown alongside nav controls
const ICON_OFFSET_PX = 14

const computeLabelMaxWidth = (maxWidth: string | number): string => {
  const base = typeof maxWidth === 'number' ? maxWidth : parseInt(maxWidth, 10)
  if (isNaN(base)) {
    return typeof maxWidth === 'string' ? maxWidth : `${maxWidth}px`
  }
  return `${Math.max(base - ICON_OFFSET_PX, 0)}px`
}

export const DrawerBreadcrumb = ({
  dataTestId,
  disabled = false,
  hideDivider = false,
  isTruncated = true,
  fontSize = '16px',
  maxWidth,
  label,
  isLoading,
  onClick,
  tooltipLabel,
}: DrawerBreadcrumbIProps) => {
  if (!label && !isLoading) {
    return null
  }

  const isLink = onClick !== undefined
  const isActiveLink = isLink && !disabled
  const labelMaxWidth: string = computeLabelMaxWidth(maxWidth)

  const labelColor = disabled ? 'brand.600' : isLink ? 'link.100' : 'inherit'
  return (
    <HStack isTruncated={isTruncated} maxWidth={maxWidth}>
      {isLoading ? (
        <Skeleton h='16px' w='40px' />
      ) : (
        // zIndex is provided to ensure popover goes above videos value of 9999 was
        // copied from existing device health popover.
        <Flex zIndex='9999'>
          <Popover
            closeDelay={250}
            isLazy
            openDelay={750}
            placement='bottom'
            trigger='hover'
          >
            <PopoverTrigger>
              <Box
                _hover={isActiveLink ? { textDecoration: 'underline' } : null}
                color={labelColor}
                cursor={isActiveLink ? 'pointer' : 'auto'}
                data-testid={dataTestId}
                fontSize={fontSize}
                fontWeight='semibold'
                isTruncated={isTruncated}
                letterSpacing='-0.53px'
                maxW={labelMaxWidth}
                onClick={isActiveLink ? onClick : null}
                px='7px'
              >
                {label}
              </Box>
            </PopoverTrigger>
            <ClickToCopyPopover
              displayText={tooltipLabel ? tooltipLabel : label}
            />
          </Popover>
        </Flex>
      )}
      {!hideDivider && (
        <Divider
          borderColor='#D7D4D4'
          borderLeftWidth='2px'
          h='20px'
          orientation='vertical'
        />
      )}
    </HStack>
  )
}
